import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { addDays, addWeeks, format, startOfWeek } from "date-fns";
import { nl } from "date-fns/locale";
import { ChevronLeft, ChevronRight, List, Pencil } from "lucide-react";
import {
  Card,
  CardTitle,
  Caption,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  Subtitle,
  VButton,
  VCheckbox,
  cn,
} from "@/components/ui";
import { useCurrentRole, useCurrentUser } from "@/domains/auth";
import { useTasks, useTasksByAssignee } from "@/domains/tasks/api/queries";
import { useUpdateTaskDueDate } from "@/domains/tasks/api/mutations";
import type { CameraTask } from "@/domains/tasks/api/types";
import { TaskCard } from "@/domains/tasks/components/taskCard";
import { TaskFormDialog } from "@/domains/tasks/components/taskFormDialog";

function DroppableDay({
  date,
  isToday,
  children,
}: {
  date: Date;
  isToday: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const iso = format(date, "yyyy-MM-dd");
  const { isOver, setNodeRef } = useDroppable({ id: iso });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex min-h-[220px] flex-col gap-2 rounded-md border bg-card p-2 transition-colors",
        isOver && "border-primary-500 bg-primary-50",
        isToday && "border-primary-300",
      )}
    >
      <div className="flex items-baseline justify-between">
        <Caption tone={isToday ? "primary" : "muted"}>
          {format(date, "EEE", { locale: nl })}
        </Caption>
        <span className={cn("text-sm font-semibold", isToday ? "text-primary-700" : "text-foreground")}>
          {format(date, "d MMM", { locale: nl })}
        </span>
      </div>
      <div className="flex flex-col gap-1.5">{children}</div>
    </div>
  );
}

function DraggableTask({
  task,
  onEdit,
}: {
  task: CameraTask;
  onEdit: (task: CameraTask) => void;
}): React.JSX.Element {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task.id,
  });
  const style: React.CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 30,
      }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("group relative", isDragging && "opacity-50")}
    >
      <div {...listeners} {...attributes} className="cursor-grab touch-none">
        <TaskCard task={task} compact />
      </div>
      <VButton
        variant="ghost"
        size="icon"
        aria-label="Bewerken"
        onClick={() => onEdit(task)}
        className="absolute right-0.5 top-0.5 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        icon={<Pencil className="size-3 text-muted-foreground" aria-hidden />}
      />
    </div>
  );
}

export function TaskCalendarPage(): React.JSX.Element {
  const role = useCurrentRole();
  const currentUser = useCurrentUser();
  const isPartner = role === "partner";
  const [onlyMine, setOnlyMine] = useState(isPartner);
  const all = useTasks();
  const mine = useTasksByAssignee(currentUser.id);
  const source = onlyMine || isPartner ? mine : all;
  const { data, isPending, isError } = source;
  const updateDueDate = useUpdateTaskDueDate();
  const [weekOffset, setWeekOffset] = useState(0);
  const [editTask, setEditTask] = useState<CameraTask | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const referenceDate = useMemo(() => addWeeks(new Date("2026-05-11"), weekOffset), [weekOffset]);
  const weekStart = useMemo(
    () => startOfWeek(referenceDate, { weekStartsOn: 1 }),
    [referenceDate],
  );
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const tasksByDay = useMemo(() => {
    const list = data ?? [];
    const map = new Map<string, Array<CameraTask>>();
    for (const d of days) {
      map.set(format(d, "yyyy-MM-dd"), []);
    }
    for (const task of list) {
      const arr = map.get(task.dueDate);
      if (arr) arr.push(task);
    }
    return map;
  }, [data, days]);

  const handleDragEnd = (event: DragEndEvent): void => {
    const taskId = String(event.active.id);
    const newDate = event.over ? String(event.over.id) : null;
    if (!newDate) return;
    const task = (data ?? []).find((t) => t.id === taskId);
    if (!task || task.dueDate === newDate) return;
    updateDueDate.mutate({ id: taskId, dueDate: newDate });
  };

  const today = format(new Date("2026-05-11"), "yyyy-MM-dd");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Taken kalender"
        subtitle="Sleep taken naar een andere dag om de deadline te wijzigen."
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {!isPartner ? (
              <VCheckbox
                id="onlyMineCal"
                checked={onlyMine}
                onCheckedChange={(c) => setOnlyMine(c === true)}
                label="Toon alleen mijn taken"
              />
            ) : null}
            <VButton variant="outline" asChild>
              <Link to="/tasks/list">
                <List className="size-4" aria-hidden />
                Lijst-view
              </Link>
            </VButton>
          </div>
        }
      />

      {isPending ? (
        <LoadingState label="Taken laden…" />
      ) : isError ? (
        <ErrorMessage message="Kon taken niet ophalen." />
      ) : (
        <>
          <Card padding="md">
            <div className="flex items-center justify-between gap-3">
              <VButton
                variant="outline"
                size="sm"
                onClick={() => setWeekOffset((w) => w - 1)}
              >
                <ChevronLeft className="size-4" aria-hidden />
                Vorige week
              </VButton>
              <CardTitle>
                Week van {format(weekStart, "d MMMM yyyy", { locale: nl })}
              </CardTitle>
              <VButton
                variant="outline"
                size="sm"
                onClick={() => setWeekOffset((w) => w + 1)}
              >
                Volgende week
                <ChevronRight className="size-4" aria-hidden />
              </VButton>
            </div>
          </Card>

          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-7">
              {days.map((day) => {
                const iso = format(day, "yyyy-MM-dd");
                const tasksOnDay = tasksByDay.get(iso) ?? [];
                return (
                  <DroppableDay key={iso} date={day} isToday={iso === today}>
                    {tasksOnDay.length === 0 ? (
                      <Subtitle className="text-xs italic text-muted-foreground/70">
                        Geen taken
                      </Subtitle>
                    ) : (
                      tasksOnDay.map((task) => (
                        <DraggableTask key={task.id} task={task} onEdit={setEditTask} />
                      ))
                    )}
                  </DroppableDay>
                );
              })}
            </div>
          </DndContext>

          {(data ?? []).length === 0 ? (
            <EmptyState title="Nog geen taken" description="Voeg taken toe vanuit een dossier." />
          ) : null}
        </>
      )}

      {editTask ? (
        <TaskFormDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditTask(null);
          }}
          mode={{ kind: "edit", task: editTask }}
        />
      ) : null}
    </div>
  );
}
